/**
 * Pure H OS · Inventory · Warehouses list + create.
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, PowerOff, ArrowRight } from "lucide-react";
import { inventory, InvWarehouse, InvMeta } from "../../services/api";

const VENTURE = "orvan";

const TYPE_LABEL: Record<string, string> = {
  raw_materials: "Raw Materials",
  packaging: "Packaging",
  wip: "WIP",
  finished_goods: "Finished Goods",
  quarantine: "Quarantine",
  showroom: "Showroom",
};

export default function WarehousesList() {
  const [rows, setRows] = useState<InvWarehouse[]>([]);
  const [meta, setMeta] = useState<InvMeta | null>(null);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [includeInactive, setIncludeInactive] = useState(false);

  const [form, setForm] = useState({
    type: "raw_materials",
    name: "",
    costCenterId: "",
    responsibleUserId: "",
    address: "",
    notes: "",
  });

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const list = await inventory.listWarehouses(VENTURE, { includeInactive });
      setRows(list);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [includeInactive]);

  useEffect(() => {
    inventory.meta().then(setMeta).catch(() => undefined);
    reload();
  }, [reload]);

  async function submit() {
    setSaving(true);
    setError(null);
    try {
      await inventory.createWarehouse(VENTURE, {
        type: form.type as any,
        name: form.name,
        costCenterId: form.costCenterId || undefined,
        responsibleUserId: form.responsibleUserId || undefined,
        address: form.address || undefined,
        notes: form.notes || undefined,
      });
      setShowAdd(false);
      setForm({ type: "raw_materials", name: "", costCenterId: "", responsibleUserId: "", address: "", notes: "" });
      reload();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  async function deactivate(id: string) {
    if (!confirm("Deactivate this warehouse? New movements will be blocked.")) return;
    try {
      await inventory.deactivateWarehouse(VENTURE, id);
      reload();
    } catch (e) {
      alert((e as Error).message);
    }
  }

  return (
    <section data-testid="warehouses-list">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-medium" style={{ color: "var(--ph-ink-strong)" }}>Warehouses</h2>
          <div className="text-xs text-slate-500 mt-1">Physical locations that hold stock.</div>
        </div>
        <div className="flex items-center gap-3">
          <label className="text-xs flex items-center gap-2 text-slate-500 cursor-pointer">
            <input
              type="checkbox"
              data-testid="wh-include-inactive"
              checked={includeInactive}
              onChange={(e) => setIncludeInactive(e.target.checked)}
            />
            Include inactive
          </label>
          <button
            data-testid="warehouse-new-btn"
            onClick={() => setShowAdd(true)}
            className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-white"
            style={{ background: "var(--ph-brand-primary)", borderRadius: "var(--ph-radius-sm)" }}
          >
            <Plus size={13} /> New warehouse
          </button>
        </div>
      </div>

      {error && (
        <div data-testid="wh-error" className="text-xs mb-3 p-2 rounded" style={{ background: "#fef2f2", color: "#b91c1c" }}>
          {error}
        </div>
      )}

      <div
        className="overflow-hidden"
        style={{ background: "var(--ph-surface-card)", border: "1px solid var(--ph-border-subtle)", borderRadius: "var(--ph-radius-md)" }}
      >
        <table className="w-full text-sm" data-testid="warehouses-table">
          <thead className="text-xs text-slate-500 uppercase tracking-wider" style={{ background: "var(--ph-surface)" }}>
            <tr>
              <th className="text-left px-3 py-2">Code</th>
              <th className="text-left px-3 py-2">Name</th>
              <th className="text-left px-3 py-2">Type</th>
              <th className="text-left px-3 py-2">Address</th>
              <th className="text-left px-3 py-2">Active</th>
              <th className="text-right px-3 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={6} className="px-3 py-6 text-center text-slate-400 text-xs">Loading…</td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={6} className="px-3 py-6 text-center text-slate-400 text-xs">No warehouses yet.</td></tr>
            )}
            {rows.map((w) => (
              <tr key={w.id} data-testid={`warehouse-row-${w.id}`} style={{ borderTop: "1px solid var(--ph-border-subtle)" }}>
                <td className="px-3 py-2 font-mono text-xs text-slate-500">{w.docNumber}</td>
                <td className="px-3 py-2 font-medium">
                  <Link to={`/pureh/inventory/warehouses/${w.id}`} className="hover:underline" data-testid={`warehouse-link-${w.id}`}>
                    {w.name}
                  </Link>
                </td>
                <td className="px-3 py-2 text-xs">{TYPE_LABEL[w.type] || w.type}</td>
                <td className="px-3 py-2 text-xs text-slate-500">{w.address || "—"}</td>
                <td className="px-3 py-2 text-xs">
                  {w.active ? (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium" style={{ background: "#dcfce7", color: "#15803d" }}>Active</span>
                  ) : (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium" style={{ background: "#f1f5f9", color: "#64748b" }}>Inactive</span>
                  )}
                </td>
                <td className="px-3 py-2 text-right">
                  <div className="inline-flex items-center gap-2">
                    <Link to={`/pureh/inventory/warehouses/${w.id}`} className="text-xs text-slate-500 hover:text-slate-800 inline-flex items-center gap-1" data-testid={`warehouse-open-${w.id}`}>
                      Open <ArrowRight size={11} />
                    </Link>
                    {w.active && (
                      <button
                        data-testid={`warehouse-deactivate-${w.id}`}
                        onClick={() => deactivate(w.id)}
                        className="text-xs text-slate-500 hover:text-red-600 inline-flex items-center gap-1"
                      >
                        <PowerOff size={11} /> Deactivate
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showAdd && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm"
          data-testid="warehouse-new-dialog"
          onClick={() => setShowAdd(false)}
        >
          <div className="w-full max-w-md p-6 mx-4 shadow-xl" style={{ background: "white", borderRadius: "var(--ph-radius-md)" }} onClick={(e) => e.stopPropagation()}>
            <h3 className="text-base font-medium mb-4">New warehouse</h3>
            <div className="space-y-3">
              <FormRow label="Type">
                <select
                  data-testid="wh-form-type"
                  className="w-full px-2 py-1.5 text-sm border rounded"
                  value={form.type}
                  onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))}
                >
                  {(meta?.warehouseTypes || Object.keys(TYPE_LABEL)).map((t) => (
                    <option key={t} value={t}>{TYPE_LABEL[t] || t}</option>
                  ))}
                </select>
              </FormRow>
              <FormRow label="Name">
                <input
                  data-testid="wh-form-name"
                  className="w-full px-2 py-1.5 text-sm border rounded"
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                />
              </FormRow>
              <FormRow label="Cost center ID (optional)">
                <input
                  data-testid="wh-form-cc"
                  className="w-full px-2 py-1.5 text-sm border rounded"
                  value={form.costCenterId}
                  onChange={(e) => setForm((f) => ({ ...f, costCenterId: e.target.value }))}
                />
              </FormRow>
              <FormRow label="Responsible (user id / email)">
                <input
                  data-testid="wh-form-responsible"
                  className="w-full px-2 py-1.5 text-sm border rounded"
                  value={form.responsibleUserId}
                  onChange={(e) => setForm((f) => ({ ...f, responsibleUserId: e.target.value }))}
                />
              </FormRow>
              <FormRow label="Address">
                <input
                  data-testid="wh-form-address"
                  className="w-full px-2 py-1.5 text-sm border rounded"
                  value={form.address}
                  onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))}
                />
              </FormRow>
              <FormRow label="Notes">
                <textarea
                  data-testid="wh-form-notes"
                  className="w-full px-2 py-1.5 text-sm border rounded"
                  value={form.notes}
                  rows={2}
                  onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                />
              </FormRow>
            </div>
            {error && (
              <div className="text-xs mt-2 text-red-600" data-testid="wh-form-error">{error}</div>
            )}
            <div className="flex justify-end gap-2 mt-5">
              <button data-testid="wh-form-cancel" onClick={() => setShowAdd(false)} className="px-3 py-1.5 text-xs border rounded">Cancel</button>
              <button
                data-testid="wh-form-save"
                onClick={submit}
                disabled={saving || !form.name.trim()}
                className="px-3 py-1.5 text-xs font-medium text-white"
                style={{ background: "var(--ph-brand-primary)", borderRadius: "var(--ph-radius-sm)", opacity: saving ? 0.6 : 1 }}
              >
                {saving ? "Saving…" : "Create"}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

function FormRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <div className="text-[11px] uppercase tracking-[0.14em] text-slate-500 mb-1">{label}</div>
      {children}
    </label>
  );
}
